import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Avatar,
  Grid,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { DeleteOutlined, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { FullTable as Table, MRTTable } from '../../../components';
import { UserContext } from '../../../context';
import { Loader, ToastV3 } from '../components';
import { SMPAPI } from '../../../fetch/smp';
import { IUser, ILicense } from '../../../models/smp/';
import { AddEditUserModal } from './include/modals';
import BulkLicenseModal from './include/modals/BulkLicenseModal';
import Checkbox from '@mui/material/Checkbox';
import { useToastContext } from '../../../hooks';

type RouteParams = { applicationId?: string };
type ViewMode = 'all' | 'inactiveUsers';


const Users: React.FC = () => {
  const { applicationId } = useParams<RouteParams>();
  const { showToast } = useToastContext();
  const { userContext } = useContext(UserContext);
  // Feature flag: enable MRT-based Users grid and bulk license actions
  const MRT_USERS_FEATURE_FLAG = 'smp-bulk-assign-license';
  const canViewBulkAssignLicense = userContext?.featureFlags?.find((flag: any) => flag?.name === MRT_USERS_FEATURE_FLAG)?.hasAccess ?? false;
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast }, [showToast]);

  const [appName, setAppName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [users, setUsers] = useState<IUser[]>([]);
  const [licenses, setLicenses] = useState<ILicense[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ViewMode>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editing, setEditing] = useState<IUser>();
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<string[]>([]);
  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [allMatchingUserIds, setAllMatchingUserIds] = useState<number[]>([]);
  const [allUsersCache, setAllUsersCache] = useState<Record<number, IUser>>({});
  const [, setLoadingAllIds] = useState(false);
  const [bulkMode, setBulkMode] = useState<'assign' | 'edit' | null>(null);
  // Derived flag matching earlier implementation
  const excludeInactiveUsers = mode === 'inactiveUsers';
  // MRT controlled states
  const [mrtSorting, setMrtSorting] = useState<any[]>([]);
  const [mrtColumnFilters, setMrtColumnFilters] = useState<any[]>([]);
  const mrtSearchDebounceRef = useRef<any>(null);
  // Removed extra date filters per request

  // Persist MRT state (sorting/filters/page) per application in localStorage
  const storageKey = React.useMemo(
    () => `smp:users:mrt:${applicationId || 'all'}`,
    [applicationId]
  );

  // Restore persisted MRT state (if feature enabled) safely
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (Array.isArray(s?.sorting)) setMrtSorting(s.sorting);
        if (Array.isArray(s?.columnFilters)) setMrtColumnFilters(s.columnFilters);
        if (typeof s?.page === 'number' && s.page >= 0) setPage(s.page);
        if (typeof s?.perPage === 'number' && s.perPage > 0) setPerPage(s.perPage);
      }
  } catch {}
  }, [storageKey]);

  useEffect(() => {
    const s = {
      sorting: mrtSorting,
      columnFilters: mrtColumnFilters,
      page,
      perPage,
      // date range filters removed
    };
  try { localStorage.setItem(storageKey, JSON.stringify(s)); } catch {}
  }, [storageKey, mrtSorting, mrtColumnFilters, page, perPage]);


  useEffect(() => {
    if (!applicationId) return;
    (async () => {
      try {
        const app = await SMPAPI.getApplication(applicationId);
        setAppName(app.name);
        const fileId = SMPAPI.getFileIdFromLogoPath(app.LogoPath);
        if (fileId) {
          setLogoUrl(await SMPAPI.getApplicationLogoUrl(fileId));
        }
      } catch {
        showToastRef.current('error', 'Failed to load application details');
      }
    })();
  }, [applicationId]);

  // Fetch licenses
  useEffect(() => {
    if (!applicationId) return;
    (async () => {
      try {
        // Fetch all licenses for the application
        const licensesRes = await SMPAPI.fetchLicenses(applicationId, { page: 1, perPage: 20000 });
        const allLicenses = licensesRes.records;

        // Fetch all users for the application
        const usersRes = await SMPAPI.fetchUsers(applicationId, { page: 1, perPage: 20000 });
        const assignedLicenseIds = new Set(
          usersRes.records.map(user => String(user.assignedLicense))
        );

        // Markd licenses as unassigned if they are not in the assignedLicenseIds set
        const updatedLicenses = allLicenses.map(license => ({
          ...license,
          unAssigned: !assignedLicenseIds.has(String(license.licenseID)),
        }));

        setLicenses(updatedLicenses);
      } catch {
        showToastRef.current('error', 'Failed to load licenses or users for filtering');
      }
    })();
  }, [applicationId, reloadCounter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [applicationId, mode, searchTerm]);

  // Clear selection when filters change or data reloads
  useEffect(() => {
    setSelectedUserIds([]);
    setAllMatchingUserIds([]);
    setAllUsersCache({});
  }, [applicationId, mode, searchTerm, selectedLicenseIds, reloadCounter]);

  // Keep MRT employment filter in sync with Hide Inactive toggle
  useEffect(() => {
    const key = 'employmentStatus';
    setMrtColumnFilters(prev => {
      const others = prev.filter((f: any) => f.id !== key && f.column !== key);
      if (excludeInactiveUsers) {
        return [...others, { id: key, value: 'Active' }];
      }
      return others;
    });
  }, [excludeInactiveUsers]);

  // Removed date filter sync effect

  // Build server query params from current UI/MRT state
  const buildUserQueryParams = useCallback((opts?: { page?: number; perPage?: number }) => {
    // Build server-side sort param from MRT sorting state for global sorting across pages
    const allowedSortIds = new Set([
      'userName',
      'email',
      'employmentStatus',
      'applicationSubStatus',
      'assignedLicenseName',
      'appTierType',
      'annualizedCost',
      'dateAdded',
      'accessRemoved',
      'licenseRemoved',
      'ytdTotals',
      'allTimeSpend',
    ]);
    const sortString = (Array.isArray(mrtSorting) ? mrtSorting : [])
      .map((s: any) => {
        const id = s.id || s.columnId || s.accessorKey;
        if (!id || !allowedSortIds.has(id)) return null;
        const dir = s.desc ? 'desc' : 'asc';
        return `${id}:${dir}`;
      })
      .filter(Boolean)
      .join(',');

    // Translate column filters to backend-native params
    const filterMap: Record<string, any> = {};
  (Array.isArray(mrtColumnFilters) ? mrtColumnFilters : []).forEach((f: any) => {
      const id = f.id || f.column;
      const val = f.value;
      if (!id) return;
      if (id === 'employmentStatus') {
        if (val === 'Active' || val === 'Inactive') filterMap.employmentStatus = val;
      }
      if (id === 'applicationSubStatus' && typeof val === 'string' && val.trim()) {
        filterMap.applicationSubStatus = val.trim();
      }
      if (id === 'appTierType' && typeof val === 'string' && val.trim()) {
        filterMap.appTierType = val.trim();
      }
      if (id === 'assignedLicenseName') {
        const values: string[] = Array.isArray(val) ? val : (val ? [val] : []);
        if (values.length) {
          const ids = licenses
            .filter(l => values.includes((l.licenseName || '').toString()))
            .map(l => String(l.licenseID));
          if (ids.length) filterMap.licenseIds = ids;
        }
      }
    });

    // Merge licenseIds from top control and MRT filter
    const mergedLicenseIds = Array.from(new Set([...(selectedLicenseIds || []), ...((filterMap.licenseIds || []) as string[])]));

    // Build params
    const pageIdx = opts?.page ?? (page + 1);
    const pageSize = opts?.perPage ?? perPage;
    return {
      page: pageIdx,
      perPage: pageSize,
      excludeInactiveUsers,
      search: filterMap.search ?? searchTerm,
      licenseIds: mergedLicenseIds,
      sort: sortString || undefined,
      applicationSubStatus: filterMap.applicationSubStatus,
      appTierType: filterMap.appTierType,
      employmentStatus: filterMap.employmentStatus,
      // date filters removed from query
    };
  }, [mrtSorting, mrtColumnFilters, selectedLicenseIds, licenses, page, perPage, excludeInactiveUsers, searchTerm]);

  const loadUsers = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const params = buildUserQueryParams();

      const res = await SMPAPI.fetchUsers(applicationId, params);
      const recs = res.records;

      const updatedUsers = recs.map(user => {
        const userLicense = licenses.find(lic => String(lic.licenseID) === String(user.assignedLicense));
        return {
          ...user,
          annualizedCost: userLicense ? parseFloat(userLicense.annualCost || '0') : 0,
          assignedLicenseName: userLicense?.licenseName || '',
        } as IUser;
      });

      setUsers(updatedUsers);
      setTotal(res.totalRecordCount);
    } catch (e) {
      showToastRef.current('error', 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [applicationId, buildUserQueryParams, licenses]);

  // Load users when page changes, reloadCounter bumps, or server searchTerm changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers, reloadCounter, page, searchTerm]);

  // Search handlers
  const triggerSearch = useCallback(() => {
    setPage(0);
    setSearchTerm(searchInput.trim());
  }, [searchInput]);

  const onSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') triggerSearch();
  }, [triggerSearch]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
  }, []);

  // Delete flow
  const requestDelete = useCallback((id: number) => {
    setPendingDelete(id);
    setConfirmOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDelete == null) return;
    try {
      await SMPAPI.deleteUser(pendingDelete);
      showToastRef.current('success', 'User removed');
      setConfirmOpen(false);
      setPendingDelete(null);
      
      // Clear selection cache
      setSelectedUserIds(prev => prev.filter(id => id !== pendingDelete));
      setAllMatchingUserIds(prev => prev.filter(id => id !== pendingDelete));
      const newCache = { ...allUsersCache };
      delete newCache[pendingDelete];
      setAllUsersCache(newCache);
      
      setReloadCounter(c => c + 1);
    } catch (e: any) {
      showToastRef.current('error', e.detail || 'Failed to delete user');
    }
  }, [pendingDelete, allUsersCache]);

  const cancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setPendingDelete(null);
  }, []);


  const handleSave = useCallback(async (u: IUser) => {
    try {
      const userToSave = { ...u, applicationID: Number(applicationId) };
      if (!u.applicationUserID) {
        delete userToSave.accessRemoved;
      }
      
      await SMPAPI.upsertUser(userToSave);
      showToastRef.current('success', u.applicationUserID ? 'User updated' : 'User added');
      setModalOpen(false);
      setReloadCounter(c => c + 1);
      
      // Clear selection cache to ensure fresh data on next select all
      setAllMatchingUserIds([]);
      setAllUsersCache({});
    } catch (e: any) {
      showToastRef.current('error', e.detail || 'Failed to save user');
    }
  }, [applicationId]);

  // Selection state derived values and helpers (moved above mrtColumns to avoid TDZ and satisfy deps)
  // Ttotal from server to determine if all are selected, falling back to cached ids
  const totalAvailableUsers = total > 0 ? total : allMatchingUserIds.length;
  const allSelected = totalAvailableUsers > 0 && selectedUserIds.length === totalAvailableUsers;
  const indeterminate = selectedUserIds.length > 0 && !allSelected;

  const fetchAllMatchingUsers = useCallback(async () => {
    if (!applicationId) return { ids: [], userMap: {} as Record<number, IUser> };
    setLoadingAllIds(true);
    try {
      const params = buildUserQueryParams({ page: 1, perPage: 20000 });
      const res = await SMPAPI.fetchUsers(applicationId, params);
      const ids = res.records.map(r => r.applicationUserID!).filter(Boolean) as number[];
      const map: Record<number, IUser> = {};
      res.records.forEach(r => { if (r.applicationUserID) map[r.applicationUserID] = r; });
      setAllMatchingUserIds(ids);
      setAllUsersCache(map);
      return { ids, userMap: map };
    } catch (e) {
      showToastRef.current('error', 'Failed to fetch all users for selection');
      return { ids: [], userMap: {} as Record<number, IUser> };
    } finally {
      setLoadingAllIds(false);
    }
  }, [applicationId, buildUserQueryParams]);

  const toggleSelectAll = useCallback(async () => {
    if (selectedUserIds.length && allMatchingUserIds.length && selectedUserIds.length === allMatchingUserIds.length) {
      setSelectedUserIds([]);
      return;
    }
    // refresh the list to include newly added records
    const { ids: fetched } = await fetchAllMatchingUsers();
    setSelectedUserIds(fetched);
  }, [selectedUserIds, allMatchingUserIds, fetchAllMatchingUsers]);

  // MRT columns (memoized) to avoid remounts that collapse the search field
  const mrtColumns = useMemo(() => {
    const unique = (arr: any[], key: string) => Array.from(new Set(arr.map(x => (x?.[key] ?? '').toString()).filter(Boolean))).sort();
    const subStatusOptions = unique(users, 'applicationSubStatus');
    const licenseNameOptions = unique(users, 'assignedLicenseName');
    const tierTypeOptions = unique(users, 'appTierType');

    const ellipsisCell = (text: any, maxWidth: number = 250) => (
      <Tooltip title={text ?? ''} arrow>
        <Box component="span" sx={{ display: 'inline-block', maxWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle', lineHeight: '1.5' }}>{text ?? ''}</Box>
      </Tooltip>
    );

    const currencyCell = (val: any) => {
      const formatted = typeof val === 'number' || (typeof val === 'string' && val !== '')
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(val))
        : '';
      return ellipsisCell(formatted, 100);
    };

    const linkCell = (text: any, maxWidth: number = 250, onClick?: () => void, href?: string, color: string = '#1976d2') => (
      <Tooltip title={text ?? ''} arrow>
        <div
          onClick={onClick}
          style={{ 
            display: 'inline-block', 
            maxWidth, 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            verticalAlign: 'middle', 
            lineHeight: '1.5',
            textDecoration: 'underline',
            fontWeight: 500,
            color,
            cursor: 'pointer',
            fontSize: 'inherit',
          }}
        >
          {href ? <a href={href} style={{ color: 'inherit', textDecoration: 'inherit' }}>{text ?? ''}</a> : (text ?? '')}
        </div>
      </Tooltip>
    );

    return [
      {
        id: 'select',
        header: '',
        enableColumnFilter: false,
        size: 40,
        muiTableHeadCellProps: { sx: { verticalAlign: 'middle', textAlign: 'center', p: '6px 8px', backgroundColor: '#005570', color: '#eff4f6' } },
        muiTableBodyCellProps: { sx: { verticalAlign: 'middle', textAlign: 'center', p: '6px 8px' } },
        Cell: ({ row }: any) => {
          const id = row.original.applicationUserID;
          const checked = id != null && selectedUserIds.includes(id);
          return (
            <Checkbox
              size="small"
              checked={!!checked}
              onChange={(_, c) => {
                if (id == null) return;
                setSelectedUserIds(prev => {
                  const set = new Set(prev);
                  if (c) set.add(id); else set.delete(id);
                  return Array.from(set);
                });
                setAllUsersCache(prev => {
                  const map = { ...prev } as Record<number, IUser>;
                  const u = users.find(u => u.applicationUserID === id);
                  if (u && u.applicationUserID) map[u.applicationUserID] = u;
                  return map;
                });
              }}
              inputProps={{ 'aria-label': 'Select row' }}
              sx={{ p: 0 }}
            />
          );
        },
        Header: () => (
          <Checkbox
            size="small"
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={toggleSelectAll}
            inputProps={{ 'aria-label': 'Select all users across all pages' }}
            sx={{ p: 0, color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }}
          />
        ),
      },
      {
        accessorKey: 'userName',
        header: 'User Name',
        filterVariant: 'text',
        size: 200,
        muiTableBodyCellProps: { sx: { maxWidth: 250, verticalAlign: 'middle', lineHeight: '1.5' } },
        Cell: ({ row }: any) => linkCell(
          row.original.userName,
          250,
          () => { setEditing(row.original); setModalOpen(true); }
        ),
      },
      {
        accessorKey: 'email',
        header: 'User Email',
        filterVariant: 'text',
        size: 250,
        muiTableBodyCellProps: { sx: { maxWidth: 380, verticalAlign: 'middle', lineHeight: '1.5' } },
        Cell: ({ cell }: any) => linkCell(
          cell.getValue() || '',
          380,
          undefined,
          `mailto:${cell.getValue() || ''}`,
          '#000000'
        ),
      },
      { accessorKey: 'employmentStatus', header: 'User Employee Status', filterVariant: 'select', filterSelectOptions: ['Active','Inactive'], size: 140, Cell: ({ cell }: any) => ellipsisCell(cell.getValue() ? (cell.getValue() === 'Active' ? 'Active Employee' : 'Inactive Employee') : '', 120) },
      { accessorKey: 'applicationSubStatus', header: 'User Application Status', filterVariant: 'select', filterSelectOptions: subStatusOptions, size: 140, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140) },
      { accessorKey: 'assignedLicenseName', header: 'Assigned Licenses', filterVariant: 'multi-select', filterSelectOptions: licenseNameOptions, size: 140, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140) },
      { accessorKey: 'appTierType', header: 'App Tier Type', filterVariant: 'select', filterSelectOptions: tierTypeOptions, size: 120, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120) },
      { accessorKey: 'annualizedCost', header: 'Annualized Cost', filterVariant: 'text', size: 120, Cell: ({ cell }: any) => currencyCell(cell.getValue()) },
      { accessorKey: 'dateAdded', header: 'Date Added', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'accessRemoved', header: 'Access Removed', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'licenseRemoved', header: 'License Removed', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      {
        id: 'ytdTotals', header: 'YTD Totals', filterVariant: 'text', size: 120,
        accessorFn: (row: any) => row.ytdValue ?? 0,
        Cell: ({ row }: any) => currencyCell(row.original.ytdValue ?? 0),
      },
      {
        id: 'allTimeSpend', header: 'All Time Spend', filterVariant: 'text', size: 130,
        accessorFn: (row: any) => row.allTimeSpend ?? 0,
        Cell: ({ row }: any) => currencyCell(row.original.allTimeSpend ?? 0),
      },
      { accessorKey: 'approvalNotes', header: 'Approval Notes', filterVariant: 'text', size: 150, enableSorting: false, Cell: ({ cell }: any) => {
        const v = (cell.getValue?.() ?? '').toString();
        const text = v.trim();
        return ellipsisCell(text, 150);
      } },
      { accessorKey: 'otherNotes', header: 'Other Notes', filterVariant: 'text', size: 150, enableSorting: false, Cell: ({ cell }: any) => {
        const v = (cell.getValue?.() ?? '').toString();
        const text = v.trim();
        return ellipsisCell(text, 150);
      } },
      { id: 'actions', header: 'Delete', size: 80, enableColumnFilter: false, muiTableBodyCellProps: { sx: { verticalAlign: 'middle', lineHeight: '1.5' } }, Cell: ({ row }: any) => {
        const id = row.original.applicationUserID;
        const isRowSelected = id != null && selectedUserIds.includes(id);
        const multipleSelected = selectedUserIds.length > 1;
        // During multi-select, hide delete icon for selected rows only; otherwise show
        if (multipleSelected && isRowSelected) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1.5' }}>
            <IconButton size="small" color="error" onClick={() => requestDelete(row.original.applicationUserID!)} sx={{ p: 0 }}>
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Box>
        );
      } },
    ];
  }, [users, requestDelete, selectedUserIds, allSelected, indeterminate, toggleSelectAll]);
  
  const caseInsensitiveSort = (rowA: any, rowB: any, columnId: string) => {
    const a = rowA.values[columnId]?.toString().toLowerCase() || '';
    const b = rowB.values[columnId]?.toString().toLowerCase() || '';
    return a.localeCompare(b);
  };


  const columns = useMemo(() => [
    {
      accessor: 'userName',
      Header: 'User Name',
      sortType: caseInsensitiveSort,
      Cell: ({ row }: any) => {
        const text = row.original.userName;
        return (
          <Tooltip title={text} arrow>
            <div
              onClick={() => { setEditing(row.original); setModalOpen(true); }}
              style={{
                display: 'inline-block',
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle',
                lineHeight: '1.5',
                textDecoration: 'underline',
                fontWeight: 500,
                color: '#1976d2',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
  },
    {
      accessor: 'email',
      Header: 'User Email',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <div
              style={{
                display: 'inline-block',
                maxWidth: 380,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle',
                lineHeight: '1.5',
                textDecoration: 'underline',
                fontWeight: 500,
                color: '#000000',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              <a href={`mailto:${text}`} style={{ color: 'inherit', textDecoration: 'inherit' }}>{text}</a>
            </div>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'employmentStatus',
      Header: 'User Employee Status',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value === 'Active' ? 'Active Employee' : 'Inactive Employee';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 120,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'applicationSubStatus',
      Header: 'User Application Status',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 140,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'assignedLicenseName',
      Header: 'Assigned Licenses',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 140,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'appTierType',
      Header: 'App Tier Type',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 120,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'annualizedCost',
      Header: 'Annualized Cost',
      sortType: (rowA, rowB, columnId) => {
        // Numeric sorting for currency values
        const a = parseFloat(rowA.values[columnId] || 0);
        const b = parseFloat(rowB.values[columnId] || 0);
        return a - b;
      },
      Cell: ({ value }: any) => {
        const formatted = typeof value === 'number' || (typeof value === 'string' && value !== '')
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 2,
            }).format(Number(value))
          : '';
        return (
          <Tooltip title={formatted} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatted}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'dateAdded',
      Header: 'Date Added',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'accessRemoved',
      Header: 'Access Removed',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'licenseRemoved',
      Header: 'License Removed',
      sortType: caseInsensitiveSort,
      Cell: ({ value }: any) => {
        const text = value || '';
        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'ytdValue',
      Header: 'YTD Totals',
      sortType: (rowA: any, rowB: any) => (rowA.original.ytdValue ?? 0) - (rowB.original.ytdValue ?? 0),
      Cell: ({ row }: any) => {
        const val = row.original.ytdValue ?? 0;
        const text = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(val);

        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'allTimeSpend',
      Header: 'All Time Spend',
      sortType: (rowA: any, rowB: any) => (rowA.original.allTimeSpend ?? 0) - (rowB.original.allTimeSpend ?? 0),
      Cell: ({ row }: any) => {
        const val = row.original.allTimeSpend ?? 0;
        const text = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(val);

        return (
          <Tooltip title={text} arrow>
            <Box
              component="span"
              sx={{
                maxWidth: 110,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'approvalNotes',
      Header: 'Approval Notes',
      disableSortBy: true,
      Cell: ({ value }: any) => (
        <Tooltip title={value} arrow>
          <Box
            component="span"
            sx={{
              maxWidth: 150,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Box>
        </Tooltip>
      ),
    },
    {
      accessor: 'otherNotes',
      Header: 'Other Notes',
      disableSortBy: true,
      Cell: ({ value }: any) => (
        <Tooltip title={value} arrow>
          <Box
            component="span"
            sx={{
              maxWidth: 150,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Box>
        </Tooltip>
      ),
    },
    {
      accessor: 'actions',
      Header: 'Delete',
      disableSortBy: true,
      Cell: ({ row }: any) => {
        const id = row.original.applicationUserID;
        const isRowSelected = id != null && selectedUserIds.includes(id);
        const multipleSelected = selectedUserIds.length > 1;
        if (multipleSelected && isRowSelected) return null;
        return (
          <Box>
            <IconButton
              size="small"
              color="error"
              onClick={() => requestDelete(row.original.applicationUserID!)}
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ], [requestDelete, selectedUserIds]);

  return (
    <>
      <Box mb={2} sx={{ bgcolor: '#f0f0f0', p: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Grid container spacing={1} alignItems="start" mb={2}>
          <Grid
            item
            xs={12}
            sm={6}
            container
            alignItems="center"
            spacing={2}
            sx={{
              '@media (max-width: 1150px)': {
                flexDirection: 'column', 
                alignItems: 'stretch',
              },
            }}
          >
            <Grid item>
              <Avatar
                variant="square"
                src={logoUrl}
                alt={appName}
                sx={{
                  bgcolor: '#ccc',
                  borderRadius: 1,
                  objectFit: 'cover',
                  height: 40,
                  width: 'auto',
                }}
              />
            </Grid>
            <Grid item>
              <Typography variant="h6">Name</Typography>
              <Typography variant="body2" color="textSecondary">
                {appName}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            container
            spacing={0.5}
            alignItems="start"
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            sx={{
              flexWrap: { xs: 'wrap', sm: 'nowrap' }, 
              gap: 0.5,
              '@media (max-width: 1150px)': {
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            }}
          >
              <Grid item xs={12} sm="auto">
                <Box sx={{
                  width: { xs: '100%', sm: 280 },
                  mr: 0.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  '@media (max-width: 1150px)': {
                    width: '100%',
                  },
                }}>
                  <Autocomplete
                    multiple
                    options={licenses.filter(license => !selectedLicenseIds.includes(String(license.licenseID)))}
                    getOptionLabel={(option) => option.licenseName || ''}
                    isOptionEqualToValue={(option, value) => option.licenseID === value.licenseID}
                    value={licenses.filter(license => selectedLicenseIds.includes(String(license.licenseID)))}
                    onChange={(event, newValue) => {
                      const updatedSelectedIds = newValue.map(license => String(license.licenseID));
                      setSelectedLicenseIds(updatedSelectedIds);
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.licenseID}
                        style={{ color: option.unAssigned ? 'gray' : 'black' }}
                      >
                        {option.licenseName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Filter by License"
                      />
                    )}
                    sx={{
                      width: '100%',
                      '& .MuiChip-root': {
                        padding: '0 0.5px',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                      },
                      '& .MuiChip-root .MuiChip-deleteIcon': {
                        fontSize: '0.8rem',
                      },
                    }}
                  />
                </Box>
        
              </Grid>
            
            {canViewBulkAssignLicense && selectedUserIds.length > 1 && (
              <Grid item xs={12} sm="auto" sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={async () => {
                    if (!allMatchingUserIds.length) {
                      await fetchAllMatchingUsers();
                    }
                    setBulkMode('assign');
                  }}
                  sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap', height: 30, minWidth: 90, px: 0.5 }}
                >
                  Bulk Assign License
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    if (!allMatchingUserIds.length) {
                      await fetchAllMatchingUsers();
                    }
                    setBulkMode('edit');
                  }}
                  sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap', height: 30, minWidth: 90, px: 0.5 }}
                >
                  Bulk Edit Licenses
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sm="auto">
              <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                onChange={(_, v) => v && setMode(v)}
                sx={{
                  '& .MuiToggleButton-root': {
                    minWidth: 90,
                    height: 30,
                    fontSize: '0.55rem',
                    whiteSpace: 'nowrap',
                    px: 0.5,
                  },
                  '@media (max-width: 1290px)': {
                    width: '100%',
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="inactiveUsers" sx={{ minWidth: 110, px: 0.5, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Hide Inactive Users
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {!canViewBulkAssignLicense && (
              <Grid item xs={12} sm="auto">
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchInput && (
                          <ClearIcon
                            fontSize="small"
                            sx={{ cursor: 'pointer', mr: 1 }}
                            onClick={clearSearch}
                          />
                        )}
                        <SearchIcon
                          fontSize="small"
                          sx={{ cursor: 'pointer' }}
                          onClick={triggerSearch}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 80,
                    height: 30,
                    fontSize: '0.72rem',
                    '& .MuiInputBase-root': { height: 30, fontSize: '0.72rem', py: 0.5 },
                    mr: 0.5,
                    '@media (max-width: 1290px)': {
                      width: '100%',
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm="auto">
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setEditing(undefined);
                  setModalOpen(true);
                }}
                sx={{
                  minWidth: 90,
                  height: 30,
                  fontSize: '0.72rem',
                  whiteSpace: 'nowrap',
                  px: 0.5,
                  '@media (max-width: 1290px)': {
                    width: '100%', 
                  },
                }}
              >
                Add User
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Box
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            position: 'relative',
            minHeight: 100,
          }}
        >
          {canViewBulkAssignLicense ? (
            <Box
              sx={{
                  minWidth: '100%',
                  overflow: 'auto',
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',           
                  background: 'white',          
                '& table': {
                    width: '100%',
                    tableLayout: 'auto',
                  },

                '& .MuiTableCell-root': {
                  fontSize: '0.75rem',
                  padding: '6px 8px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '250px', 
                  borderBottom: '1px solid rgb(212, 210, 210)', 
                },
                '& .MuiTableCell-head': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  overflowWrap: 'normal',
                  wordWrap: 'normal',
                  wordBreak: 'keep-all',
                  hyphens: 'manual',
                  maxWidth: '150px',
                  transition: 'color 0.3s ease',
                  borderBottom: '2px solid rgb(212, 210, 210)',

                },
                '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
                  color: '#64b5f6',
                  cursor: 'pointer',
                },
                '& th:last-of-type, & td:last-of-type': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  textAlign: 'center ! important',
                },
                'td:last-of-type': {
                  p: 0,
                  backgroundColor: '#fff',
                },
                '& .MuiTableCell-body': { py: 0.1 },
                '&::-webkit-scrollbar': { height: 2 },
                '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#a0a0a0',
                  borderRadius: 3,
                },
                '& .MuiTableCell-root.emailColumn': {
                  maxWidth: 'none',
                  overflow: 'visible',
                },
                '& .MuiTableCell-head.emailColumn': {
                  maxWidth: 'none',
                },
                '& th:nth-of-type(2), & td:nth-of-type(2)': {
                  minWidth: '200px !important',
                  width: '250px !important',
                  maxWidth: '250px !important',
                },
                // No explicit width for the first column (checkboxes)
                scrollbarHeight: 'thin',
                scrollbarColor: '#fff #fff',
              }}
            >
              {(() => {
                  return (
                    <MRTTable
                      columns={mrtColumns as any}
                      data={users as any}
                      loading={loading}
                      manualPagination
                      manualSorting
                      leftTopToolbar={selectedUserIds.length > 0 ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.1, transform: 'translateY(3px)', mt: '2px' }}>
                            {selectedUserIds.length} selected
                          </Typography>
                        </Box>
                      ) : null}
                      // enforce exact-match global filter in the MRT wrapper
                      totalRowCount={total}
                      pageIndex={page}
                      pageSize={perPage}
                      onPaginationChange={(updater: any) => {
                        const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
                        if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
                        if (typeof next?.pageSize === 'number') setPerPage(next.pageSize);
                      }}
                      onSortingChange={(updater: any) => {
                        const next = typeof updater === 'function' ? updater(mrtSorting) : updater;
                        setMrtSorting(next || []);
                        // Reset to first page so server can repaginate after sort changes
                        setPage(0);
                      }}
                      // Wire MRT global filter to local state (exact match client filter)
                      globalFilter={searchInput}
                      onGlobalFilterChange={(val: any) => {
                        const v = typeof val === 'string' ? val : '';
                        setSearchInput(v);
                        // debounce server search: if 2+ chars, update searchTerm after 250ms
                        if (mrtSearchDebounceRef.current) {
                          clearTimeout(mrtSearchDebounceRef.current);
                        }
                        mrtSearchDebounceRef.current = setTimeout(() => {
                          if (v.trim().length >= 2 || v.trim().length === 0) {
                            setPage(0);
                            setSearchTerm(v.trim());
                          }
                        }, 250);
                      }}
                      sorting={mrtSorting}
                      columnFilters={mrtColumnFilters}
                      onColumnFiltersChange={(updater: any) => {
                        const next = typeof updater === 'function' ? updater(mrtColumnFilters) : updater;
                        setMrtColumnFilters(next);
                        // avoid double reset; searchTerm change or user action will handle page when needed
                        const st = (next || []).find((f: any) => (f.id || f.column) === 'employmentStatus');
                        if (st && st.value === 'Active') setMode('inactiveUsers');
                        else if (!st) setMode('all');
                      }}
                      // MRT built-in row selection disabled; using custom selection column
                    />
                  );
                })()}
            </Box>
          ) : (
            loading ? (
              <Loader />
            ) : (
              <Box
                sx={{
                    minWidth: '100%',
                    overflow: 'auto',
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px',           
                    background: 'white',          
                  '& table': {
                      width: '100%',
                      tableLayout: 'auto',
                    },

                  '& .MuiTableCell-root': {
                    fontSize: '0.75rem',
                    padding: '6px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px', 
                    borderBottom: '1px solid rgb(212, 210, 210)', 
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 'bold',
                    whiteSpace: 'normal',
                    // Wrap between words only; don't break individual word characters
                    overflowWrap: 'normal',
                    wordWrap: 'normal',
                    wordBreak: 'keep-all',
                    hyphens: 'manual',
                    maxWidth: '150px',
                    transition: 'color 0.3s ease',
                    borderBottom: '2px solid rgb(212, 210, 210)',

                  },
                  '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
                    color: '#64b5f6',
                    cursor: 'pointer',
                  },
                  '& th:last-of-type, & td:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    textAlign: 'center ! important',
                  },
                  'td:last-of-type': {
                    p: 0,
                    backgroundColor: '#fff',
                  },
                  '& .MuiTableCell-body': { py: 0.1 },
                  '&::-webkit-scrollbar': { height: 2 },
                  '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#a0a0a0',
                    borderRadius: 3,
                  },
                  '& .MuiTableCell-root.emailColumn': {
                    maxWidth: 'none',
                    overflow: 'visible',
                  },
                  '& .MuiTableCell-head.emailColumn': {
                    maxWidth: 'none',
                  },
                  '& th:nth-child(2), & td:nth-child(2)': {
                    minWidth: '200px !important',
                    width: '250px !important',
                    maxWidth: '20px !important',
                  },
                  '& th:first-of-type, & td:first-of-type': {
                    minWidth: '250px !important',
                    width: '250px !important',
                    maxWidth: '250px !important',
                    paddingLeft: '5px !important',
                    textAlign: 'left !important',
                  },
                  scrollbarHeight: 'thin',
                  scrollbarColor: '#fff #fff',
                }}
              >
                <Table
                  columns={columns}
                  data={users}
                  isLoading={loading}
                  serverPage={page}
                  serverPerPage={perPage}
                  serverRecordCount={total}
                  handlePage={setPage}
                  handleRowsPerPage={setPerPage}
                  // ensure stable keys if legacy table uses row.id
                  // rowId={(row: any, index: number) => row?.applicationUserID ?? index}
                />
              </Box>
            )
          )}
        </Box>
      </Box>
      

      <AddEditUserModal
        open={modalOpen}
        initialData={editing}
        applicationID={Number(applicationId)}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />

      <BulkLicenseModal
        open={bulkMode !== null}
        mode={bulkMode || 'assign'}
        applicationID={Number(applicationId)}
        selectedUserIds={selectedUserIds}
        usersMap={allUsersCache}
        onClose={() => setBulkMode(null)}
        onCompleted={() => { 
          setSelectedUserIds([]); 
          setAllMatchingUserIds([]);
          setAllUsersCache({});
          setReloadCounter(c => c + 1); 
        }}
        showToast={(type, msg) => showToastRef.current(type, msg)}
      />

      {confirmOpen && (
        <ToastV3
          type="error"
          message="Delete this user?"
          confirmText="Delete"
          showReset={false}
          onCancel={cancelDelete}
          onSave={confirmDelete}
          onClose={cancelDelete}
        />
      )}
    </>
  );
};

export default Users;